/**
 * Городская область доступа к разделу «Оборудование».
 *
 * Приходит с бэкенда в GET /inventory/source-data вместе со справочниками:
 *
 *   scope: { view_city_ids, edit_city_ids, export_city_ids }
 *
 * Каждое значение читается одинаково:
 *   null        — ограничения нет, доступно любое оборудование, включая без города;
 *   []          — не доступно ничего;
 *   [1, 2, …]   — только эти города; оборудование без города НЕ входит.
 *
 * Те же три списка и то же правило работают на бэкенде (App\Services\Inventory\InventoryAccess).
 * Фронт не знает ни ролей, ни групп безопасности и доступ по ним не вычисляет: разрешение
 * приходит возможностями (6.3-C1), область — этим контрактом.
 *
 * ОБЛАСТЬ У КАЖДОГО РАЗРЕШЕНИЯ СВОЯ, и складывать их нельзя. Наблюдатель из городской
 * группы видит все города, а править может только свой: одного списка городов хватило бы,
 * чтобы показать ему кнопку «Редактировать» там, где сервер ответит отказом.
 *
 * FAIL CLOSED. Отсутствие контракта, неожиданная форма ответа, ещё не доехавший запрос —
 * всё это «ничего нельзя», а не «можно всё». Единственное, что даёт полный доступ, —
 * явный null от бэкенда.
 */

/**
 * Область без ограничения. Именно null, а не пустой массив и не undefined.
 */
export const UNRESTRICTED = null;

/**
 * Закрытая область: до ответа бэкенда доступа нет.
 *
 * @returns {{viewCityIds: Array, editCityIds: Array, exportCityIds: Array}}
 */
export function emptyInventoryScope() {
    return {
        viewCityIds: [],
        editCityIds: [],
        exportCityIds: [],
    };
}

/**
 * Один список городов из контракта.
 *
 * @param {*} value
 * @returns {null|number[]} null — без ограничения; массив — только эти города.
 */
function normalizeCityIds(value) {
    if (value === null) {
        return UNRESTRICTED;
    }

    if (!Array.isArray(value)) {
        // undefined, объект, строка — контракт не тот, которого мы ждём. Молча считать
        // это «без ограничения» значило бы открыть весь инвентарь на первой же опечатке
        // в ответе.
        return [];
    }

    return value.map((id) => Number(id)).filter((id) => Number.isInteger(id));
}

/**
 * Контракт области из ответа source-data.
 *
 * @param {*} raw Объект scope из ответа или что угодно вместо него.
 * @returns {{viewCityIds: (null|number[]), editCityIds: (null|number[]), exportCityIds: (null|number[])}}
 */
export function normalizeInventoryScope(raw) {
    if (!raw || typeof raw !== 'object') {
        return emptyInventoryScope();
    }

    return {
        viewCityIds: normalizeCityIds(raw.view_city_ids),
        editCityIds: normalizeCityIds(raw.edit_city_ids),
        exportCityIds: normalizeCityIds(raw.export_city_ids),
    };
}

/**
 * Попадает ли город в область.
 *
 * Оборудование без города (city_id = null) проходит только в неограниченную область:
 * позиция без города не принадлежит ни одному филиалу, и городскому пользователю она
 * недоступна — ровно как на бэкенде.
 *
 * @param {null|number[]} cityIds Область из контракта.
 * @param {*} cityId Город позиции: inventory_items.city_id, а НЕ то, что показано в карточке.
 * @returns {boolean}
 */
export function cityInScope(cityIds, cityId) {
    if (cityIds === UNRESTRICTED) {
        return true;
    }

    if (!Array.isArray(cityIds)) {
        return false;
    }

    if (cityId === null || cityId === undefined || cityId === '') {
        return false;
    }

    return cityIds.indexOf(Number(cityId)) !== -1;
}

/**
 * Города справочника, попадающие в область, — для выпадающих списков.
 *
 * @param {Array} cities Список вида [{ id, name }].
 * @param {null|number[]} cityIds
 * @returns {Array}
 */
export function scopedCities(cities, cityIds) {
    const options = Array.isArray(cities) ? cities : [];

    if (cityIds === UNRESTRICTED) {
        return options;
    }

    if (!Array.isArray(cityIds)) {
        return [];
    }

    return options.filter((city) => cityIds.indexOf(Number(city.id)) !== -1);
}

/**
 * Единственный город области — или null.
 *
 * Нужен создания: если править можно ровно один город, выбирать пользователю нечего,
 * и поле подставляется само. Неограниченная область единственного города не имеет,
 * даже когда в справочнике он один: менеджер вправе оставить позицию без города.
 *
 * @param {null|number[]} cityIds
 * @returns {null|number}
 */
export function onlyCityId(cityIds) {
    return Array.isArray(cityIds) && cityIds.length === 1 ? cityIds[0] : null;
}

/**
 * Сохранённые в localStorage города фильтра, которые всё ещё доступны.
 *
 * Состав городских групп пользователя меняется в AD, и сохранённый фильтр переживает
 * это изменение. Города, которых больше нет в справочнике раздела, нужно убрать: иначе
 * в чипах висит пункт без названия, а в запрос уходит город, которого пользователь
 * всё равно не увидит.
 *
 * @param {Array} selectedIds Сохранённые идентификаторы.
 * @param {Array} cities Справочник, уже сужённый бэкендом по области просмотра.
 * @returns {number[]}
 */
export function availableCityIds(selectedIds, cities) {
    const available = (Array.isArray(cities) ? cities : []).map((city) => Number(city.id));

    return (Array.isArray(selectedIds) ? selectedIds : [])
        .map((id) => Number(id))
        .filter((id) => available.indexOf(id) !== -1);
}
